import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessions } from '../api';
import { Phone, PhoneIncoming, PhoneOutgoing, Globe, Clock, Search, RefreshCw, Trash2, Database, Activity, AlertTriangle, Globe as GlobeIcon, Timer, DollarSign, TrendingUp, ArrowRightLeft, Gauge } from 'lucide-react';
import Toast from './Toast';
import VisitorBadge from './VisitorBadge';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';

const SessionsList = () => {
  const navigate = useNavigate();
  const [sessionsList, setSessionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sessionsPerPage] = useState(10);
  const [transferModal, setTransferModal] = useState({ open: false, session: null });
  const [transferData, setTransferData] = useState({ target_number: '', transfer_type: 'warm' });
  const [transferring, setTransferring] = useState(false);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [stats, setStats] = useState({
    total_sessions: 0,
    active_sessions: 0,
    abandoned_sessions: 0,
    web_sessions: 0,
    avg_duration: 0,
    total_minutes: 0,
    total_cost_usd: 0,
    avg_cost_usd: 0,
    avg_latency_ms: null
  });
  const [healthStats, setHealthStats] = useState({
    total_active: 0,
    healthy_active: 0,
    stale_sessions: 0,
    very_stale_sessions: 0,
    health_percentage: 100
  });

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await sessions.list(currentPage, sessionsPerPage);
      const data = response.data;
      setSessionsList(data.sessions);
      setTotalSessions(data.total);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await sessions.stats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadHealthStats = async () => {
    try {
      const response = await sessions.health();
      setHealthStats(response.data);
    } catch (error) {
      console.error('Failed to load health stats:', error);
    }
  };

  const handleCleanup = async () => {
    setShowCleanupModal(false);
      try {
        const response = await sessions.cleanup();
      setToast({ message: `Cleaned up ${response.data.abandoned_count} abandoned sessions`, type: 'success' });
        loadSessions();
        loadStats();
        loadHealthStats();
      } catch (error) {
        console.error('Failed to cleanup sessions:', error);
      setToast({ message: 'Failed to cleanup sessions', type: 'error' });
    }
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalSessions / sessionsPerPage);

  useEffect(() => {
    loadSessions();
    loadStats();
    loadHealthStats();
  }, [currentPage]);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const EST_TIMEZONE = 'America/New_York';

  // Force UTC parsing — DB timestamps may lack the 'Z' suffix, causing browsers
  // to treat them as local time. Appending 'Z' ensures correct UTC interpretation
  // before converting to EST/EDT.
  const toUTC = (dateString) => {
    if (!dateString) return null;
    const s = dateString.trim();
    return new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
  };

  const formatDate = (dateString) => {
    const date = toUTC(dateString);
    if (!date) return 'N/A';
    return date.toLocaleString('en-US', { timeZone: EST_TIMEZONE });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = toUTC(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { timeZone: EST_TIMEZONE });
  };

  const formatPhoneNumber = (number) => {
    if (!number) return 'N/A';
    // Format E.164 to readable format: +1 (555) 123-4567
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
      }
    }
    return number;
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-primary/20 text-primary border-primary/30';
      case 'ended':
      case 'completed':
        return 'bg-success/20 text-success border-success/30';
      case 'failed':
      case 'error':
      case 'terminated':
        return 'bg-destructive/20 text-destructive border-destructive/30';
      case 'abandoned':
        return 'bg-warning/20 text-warning border-warning/30';
      case 'voicemail_reached':
        return 'bg-blue-500/20 text-blue-600 border-blue-500/30 dark:text-blue-400';
      default:
        return 'bg-muted/20 text-muted-foreground border-border';
    }
  };

  const getSentimentColor = (sentiment) => {
    if (!sentiment) return 'text-muted-foreground';
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return 'text-success';
      case 'negative':
        return 'text-destructive';
      case 'neutral':
      default:
        return 'text-foreground';
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === 'inbound' ? <PhoneIncoming className="w-4 h-4" /> : <PhoneOutgoing className="w-4 h-4" />;
  };

  const getDirectionColor = (direction) => {
    return direction === 'inbound' ? 'text-success' : 'text-primary';
  };

  const getSourceIcon = (channel) => {
    return channel === 'phone' ? <Phone className="w-4 h-4" /> : <Globe className="w-4 h-4" />;
  };

  // Helper to get TTS provider name from cost breakdown
  const getTTSProvider = (costBreakdown) => {
    // First check if providers field exists (new format)
    if (costBreakdown.providers?.tts) {
      const provider = costBreakdown.providers.tts;
      const ttsModel = costBreakdown.providers?.tts_model;
      
      // For ElevenLabs, include the model name
      if (provider === 'elevenlabs' && ttsModel) {
        // Map model names to display names
        if (ttsModel === 'eleven_monolingual_v1') {
          return 'ElevenLabs Monolingual';
        } else if (ttsModel === 'eleven_multilingual_v2') {
          return 'ElevenLabs Multilingual';
        } else {
          return `ElevenLabs (${ttsModel})`;
        }
      }
      
      // For other providers, capitalize first letter
      if (provider === 'cartesia') {
        return 'Cartesia';
      }
      if (provider === 'fishaudio') {
        return 'Fish Audio';
      }
      
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
    // Fallback: infer from rates_used (old format)
    if (costBreakdown.rates_used?.tts) {
      const ttsProviders = Object.keys(costBreakdown.rates_used.tts);
      if (ttsProviders.length > 0) {
        const provider = ttsProviders[0];
        // Capitalize first letter for display
        return provider.charAt(0).toUpperCase() + provider.slice(1);
      }
    }
    return 'TTS';
  };

  // Helper to get LLM model name from cost breakdown
  const getLLMModel = (costBreakdown) => {
    // First check if providers field exists (new format)
    if (costBreakdown.providers?.llm) {
      return costBreakdown.providers.llm;
    }
    // Fallback: infer from rates_used (old format) - just show "LLM" for old sessions
    return 'LLM';
  };

  // Helper to get STT provider name from cost breakdown
  const getSTTProvider = (costBreakdown) => {
    // First check if providers field exists (new format)
    if (costBreakdown.providers?.stt) {
      const provider = costBreakdown.providers.stt;
      const sttModel = costBreakdown.providers?.stt_model;
      
      // For Deepgram Nova-3, include the model name
      if (provider === 'deepgram_nova2' || provider === 'deepgram' || sttModel) {
        if (sttModel === 'nova3_mono_en') {
          return 'Nova-3 Mono English';
        } else if (sttModel === 'nova3_multi') {
          return 'Nova-3 Multilingual';
        } else if (provider === 'deepgram_nova2') {
          return 'Deepgram Nova-2';
        } else {
          return 'Deepgram';
        }
      }
      
      return provider === 'deepgram_nova2' ? 'Deepgram Nova-2' : provider;
    }
    // Fallback: infer from rates_used (old format)
    if (costBreakdown.rates_used?.stt) {
      const sttProviders = Object.keys(costBreakdown.rates_used.stt);
      if (sttProviders.length > 0) {
        const provider = sttProviders[0];
        if (provider === 'nova3_mono_en') {
          return 'Nova-3 Mono English';
        } else if (provider === 'nova3_multi') {
          return 'Nova-3 Multilingual';
        } else if (provider === 'deepgram_nova2') {
          return 'Deepgram Nova-2';
        }
        return provider;
      }
    }
    return 'STT';
  };

  const handleDownloadRecording = (recordingUrl, sessionId) => {
    if (recordingUrl) {
      const link = document.createElement('a');
      link.href = recordingUrl;
      link.download = `recording-${sessionId}.ogg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePlayRecording = (recordingUrl) => {
    if (recordingUrl) {
      const audio = new Audio(recordingUrl);
      audio.play().catch(err => {
        console.error('Failed to play recording:', err);
        setToast({ message: 'Failed to play recording', type: 'error' });
      });
    }
  };

  // Phase 4: Transfer functionality
  const openTransferModal = (session) => {
    setTransferModal({ open: true, session });
    setTransferData({ target_number: '', transfer_type: 'warm' });
    setMessage('');
  };

  const closeTransferModal = () => {
    setTransferModal({ open: false, session: null });
    setTransferData({ target_number: '', transfer_type: 'warm' });
    setMessage('');
  };

  const handleTransfer = async () => {
    if (!transferData.target_number) {
      setMessage('Please enter a target phone number');
      return;
    }

    if (!transferData.target_number.startsWith('+')) {
      setMessage('Phone number must be in E.164 format (e.g., +15551234567)');
      return;
    }

    try {
      setTransferring(true);
      setMessage('');
      
      const response = await sessions.transfer(transferModal.session.id, transferData);
      setMessage(`Transfer initiated successfully! Transfer ID: ${response.data.transfer_id}`);
      
      setTimeout(() => {
        loadSessions();
        closeTransferModal();
      }, 2000);
      
    } catch (error) {
      setMessage('Transfer failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTransferring(false);
    }
  };

  if (loading && sessionsList.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading sessions..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground mb-1">Call History</h2>
        <p className="text-sm text-muted-foreground">Complete log of all voice interactions.</p>
      </div>

      {/* Summary Stats */}
      {stats.total_sessions > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Database className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-foreground">
              {stats.total_sessions}
                </div>
                <div className="text-xs text-muted-foreground">Total Sessions</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Activity className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-success">
              {stats.active_sessions}
                </div>
                <div className="text-xs text-muted-foreground">Active Now</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <AlertTriangle className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-warning">
              {stats.abandoned_sessions}
                </div>
                <div className="text-xs text-muted-foreground">Abandoned</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <GlobeIcon className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-primary">
              {stats.web_sessions}
                </div>
                <div className="text-xs text-muted-foreground">Web Sessions</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Timer className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-secondary-cyan">
              {stats.avg_duration}s
                </div>
                <div className="text-xs text-muted-foreground">Avg Duration</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <DollarSign className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-success">
              ${(stats.total_cost_usd || 0).toFixed(4)}
                </div>
                <div className="text-xs text-muted-foreground">Total Cost</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <TrendingUp className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-primary">
              ${(stats.avg_cost_usd || 0).toFixed(4)}
                </div>
                <div className="text-xs text-muted-foreground">Avg Cost</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Clock className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-secondary-cyan">
              {(stats.total_minutes || 0).toFixed(2)}
                </div>
                <div className="text-xs text-muted-foreground">Total Minutes</div>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden border border-border/50 bg-card rounded-lg hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:border-primary/20 p-3">
            <div className="flex items-center gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                <Gauge className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xl font-bold text-secondary-cyan">
              {stats.avg_latency_ms != null
                ? `${stats.avg_latency_ms}ms (${(Number(stats.avg_latency_ms) / 1000).toFixed(2)}s)`
                : 'N/A'}
                </div>
                <div className="text-xs text-muted-foreground">Avg Latency</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Health Stats */}
      {healthStats.total_active > 0 && (
        <div className="bg-card/80 border border-border rounded-lg p-3 mb-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Session Health Monitor</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className={`text-2xl font-bold ${healthStats.health_percentage >= 80 ? 'text-success' : healthStats.health_percentage >= 60 ? 'text-warning' : 'text-destructive'}`}>
                {healthStats.health_percentage}%
              </div>
              <div className="text-muted-foreground text-sm">Health Score</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success">{healthStats.healthy_active}</div>
              <div className="text-muted-foreground text-sm">Healthy Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning">{healthStats.stale_sessions}</div>
              <div className="text-muted-foreground text-sm">Stale (5-15min)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">{healthStats.very_stale_sessions}</div>
              <div className="text-muted-foreground text-sm">Very Stale (15min+)</div>
            </div>
          </div>
          {(healthStats.stale_sessions > 0 || healthStats.very_stale_sessions > 0) && (
            <div className="mt-3 p-3 bg-warning/10 border border-warning/30 rounded-lg">
              <div className="text-warning text-sm">
                ⚠️ {healthStats.stale_sessions + healthStats.very_stale_sessions} sessions may be abandoned. 
                Use "Cleanup Abandoned" button to mark them as terminated.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-4">
        <button
          onClick={() => {
            loadSessions();
            loadStats();
            loadHealthStats();
          }}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
        <button
          onClick={() => setShowCleanupModal(true)}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
        >
          <Trash2 className="w-4 h-4" />
          Cleanup Abandoned Sessions
        </button>
      </div>

      {/* Call History Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
      <div className="overflow-x-auto max-w-full">
          <table className="w-full" style={{ minWidth: '1000px' }}>
          <thead className="sticky top-0 z-10">
              <tr className="border-b border-border bg-muted/30 dark:bg-muted/50">
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[100px]">Direction</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[140px]">Phone Number</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[100px]">Source</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[170px]">Visitor</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[120px]">Agent</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[90px]">Duration</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[90px]"> End to End Latency</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[110px]">Status</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[100px]">Sentiment</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[140px]">Time</th>
                <th className="text-left py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[280px]">Recording</th>
                <th className="text-right py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[140px]">Cost</th>
                <th className="text-center py-4 px-3 text-xs font-semibold text-foreground uppercase tracking-wider whitespace-nowrap min-w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
              {sessionsList.map((session) => {
                const phoneNumber = session.caller_number || session.called_number;
                const isActive = session.status.toLowerCase() === 'active';
                
                return (
                  <tr key={session.id} className="border-b border-border/30 hover:bg-muted/20 dark:hover:bg-muted/30 transition-all duration-150 group">
                    {/* Direction */}
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-2">
                        <span className={getDirectionColor(session.call_direction || 'inbound')}>
                          {getDirectionIcon(session.call_direction || 'inbound')}
                    </span>
                        <span className="text-sm font-medium text-foreground capitalize whitespace-nowrap">
                          {session.call_direction || 'inbound'}
                    </span>
                  </div>
                </td>

                    {/* Phone Number */}
                    <td className="py-4 px-3">
                      <span className="text-sm text-foreground truncate block font-mono max-w-[140px]">
                        {session.channel === 'phone' ? formatPhoneNumber(phoneNumber) : '-'}
                      </span>
                    </td>

                    {/* Source */}
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0">
                          {getSourceIcon(session.channel)}
                    </span>
                        <span className="text-sm text-foreground capitalize font-medium whitespace-nowrap">
                          {session.channel}
                    </span>
                  </div>
                </td>

                    {/* Visitor (widget sessions: country, IP, browser) */}
                    <td className="py-4 px-3">
                      <VisitorBadge visitor={session.visitor} />
                    </td>

                    {/* Agent */}
                    <td className="py-4 px-3">
                      <span className="text-sm text-foreground truncate block font-medium max-w-[120px]">
                        {session.agent_name || 'Unknown'}
                      </span>
                </td>

                    {/* Duration */}
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                        <span className="text-sm text-foreground font-medium">
                          {formatDuration(session.duration)}
                    </span>
                  </div>
                </td>

                    {/* Latency */}
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Gauge className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                        {(() => {
                          if (session.avg_response_time_ms == null) return <span className="text-sm font-medium text-muted-foreground">-</span>;
                          const val = Math.round(session.avg_response_time_ms);
                          const sec = (session.avg_response_time_ms / 1000).toFixed(2);
                          const color = val <= 1400 ? 'text-success' : 'text-warning';
                          return <span className={`text-sm font-medium ${color}`}>{val}ms ({sec}s)</span>;
                        })()}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-4 px-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border whitespace-nowrap shadow-sm ${getStatusColor(session.status)}`}>
                          {session.status === 'VOICEMAIL_REACHED' ? 'Voicemail Reached' : session.status.toLowerCase()}
                        </span>
                        {isActive && (
                          <div className="flex gap-0.5 flex-shrink-0">
                            {[1, 2, 3].map((i) => (
                              <div
                                key={i}
                                className="w-1 h-4 bg-primary rounded-full animate-pulse shadow-sm shadow-primary/50"
                                style={{ animationDelay: `${i * 0.1}s` }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Sentiment */}
                    <td className="py-4 px-3">
                      <span className={`text-sm font-semibold capitalize px-2 py-1 rounded whitespace-nowrap ${getSentimentColor(session.sentiment)}`}>
                        {session.sentiment || 'Neutral'}
                  </span>
                </td>

                    {/* Time */}
                    <td className="py-4 px-3">
                  <div className="flex flex-col gap-0.5 min-w-[140px]">
                        <span className="text-sm text-foreground font-medium whitespace-nowrap">
                      {formatDate(session.started_at)}
                    </span>
                    {session.ended_at && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                        Ended: {formatDate(session.ended_at)}
                      </span>
                    )}
                  </div>
                </td>

                    {/* Recording */}
                    <td className="py-4 px-3 min-w-[280px]">
         {session.recording_url ? (
                        isActive ? (
                          <span className="text-sm text-muted-foreground italic whitespace-nowrap">Recording...</span>
                        ) : (
           <div className="flex justify-center">
                            <audio controls className="w-full max-w-[260px] h-10 rounded-lg">
               <source src={sessions.recordingUrl(session.id)} type="audio/ogg" />
               <source src={sessions.recordingUrl(session.id)} type="audio/mpeg" />
               Your browser does not support the audio element.
             </audio>
           </div>
                        )
                      ) : session.status.toLowerCase() === 'failed' || session.status.toLowerCase() === 'error' ? (
                        <span className="text-sm text-muted-foreground italic whitespace-nowrap">Not available</span>
         ) : (
                        <span className="text-sm text-muted-foreground whitespace-nowrap">-</span>
         )}
       </td>

                    {/* Cost */}
                    <td className="py-4 px-3 text-right min-w-[140px]">
                  {session.cost_usd ? (
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-sm text-success font-semibold">
                        ${session.cost_usd.toFixed(4)}
                      </span>
                      {session.cost_breakdown && (
                        <div className="text-muted-foreground text-xs space-y-0.5 mt-1">
                          {session.cost_breakdown.costs?.llm_cost > 0 && (
                            <div className="flex justify-end items-center space-x-1.5">
                              <span className="text-primary dark:text-primary font-medium">
                                {getLLMModel(session.cost_breakdown)}:
                              </span>
                              <span className="text-foreground/80">${session.cost_breakdown.costs.llm_cost.toFixed(4)}</span>
                            </div>
                          )}
                          {session.cost_breakdown.costs?.tts_cost > 0 && (
                            <div className="flex justify-end items-center space-x-1.5">
                              <span className="text-secondary-cyan dark:text-secondary-cyan font-medium capitalize">
                                {getTTSProvider(session.cost_breakdown)}:
                              </span>
                              <span className="text-foreground/80">${session.cost_breakdown.costs.tts_cost.toFixed(4)}</span>
                            </div>
                          )}
                          {session.cost_breakdown.costs?.stt_cost > 0 && (
                            <div className="flex justify-end items-center space-x-1.5">
                              <span className="text-secondary-cyan dark:text-secondary-cyan font-medium">
                                {getSTTProvider(session.cost_breakdown)}:
                              </span>
                              <span className="text-foreground/80">${session.cost_breakdown.costs.stt_cost.toFixed(4)}</span>
                            </div>
                          )}
                          {session.cost_breakdown.costs?.telephony_cost > 0 && (
                            <div className="flex justify-end items-center space-x-1.5">
                              <span className="text-success dark:text-success font-medium">Telephony:</span>
                              <span className="text-foreground/80">${session.cost_breakdown.costs.telephony_cost.toFixed(4)}</span>
                            </div>
                          )}
                          {session.cost_breakdown.costs?.avatar_cost > 0 && (
                            <div className="flex justify-end items-center space-x-1.5">
                              <span className="text-purple-500 dark:text-purple-400 font-medium">LiveAvatar:</span>
                              <span className="text-foreground/80">${session.cost_breakdown.costs.avatar_cost.toFixed(4)}</span>
                            </div>
                          )}
                          {session.cost_breakdown.costs?.infrastructure_cost > 0 && (
                            <div className="flex justify-end items-center space-x-1.5">
                              <span className="text-muted-foreground font-medium text-xs">Infrastructure:</span>
                              <span className="text-xs text-foreground/70">${session.cost_breakdown.costs.infrastructure_cost.toFixed(4)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                  )}
                </td>

                    {/* Actions */}
                    <td className="py-4 px-3 text-center min-w-[120px]">
                      <div className="flex justify-center gap-2 flex-wrap">
                    {session.status === 'ACTIVE' && session.channel === 'phone' && (
                      <button
                        onClick={() => openTransferModal(session)}
                            className="px-2.5 py-1.5 bg-primary/10 dark:bg-primary/20 border border-primary/30 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 dark:hover:bg-primary/30 hover:border-primary/50 transition-all duration-150 flex items-center gap-1.5 shadow-sm hover:shadow-md whitespace-nowrap"
                        title="Transfer Call"
                      >
                            <ArrowRightLeft className="w-3 h-3" />
                            Transfer
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate(`/admin?section=session-analysis&sessionId=${session.id}`);
                      }}
                          className="px-2.5 py-1.5 bg-success/10 dark:bg-success/20 border border-success/30 text-success rounded-lg text-xs font-medium hover:bg-success/20 dark:hover:bg-success/30 hover:border-success/50 transition-all duration-150 flex items-center gap-1.5 shadow-sm hover:shadow-md whitespace-nowrap"
                      title="View Analysis"
                    >
                          <Search className="w-3 h-3" />
                          Analysis
                    </button>
                  </div>
                </td>
              </tr>
                );
              })}
          </tbody>
        </table>
        
        {sessionsList.length === 0 && totalSessions === 0 && (
            <div className="text-center py-16">
              <div className="text-muted-foreground text-sm">
                No sessions found. Sessions will appear here as users connect.
              </div>
            </div>
        )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground order-2 sm:order-1">
            Showing {((currentPage - 1) * sessionsPerPage) + 1} to {Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions} sessions
          </div>
          
          <div className="flex space-x-2 order-1 sm:order-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-card border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition-colors"
            >
              Previous
            </button>
            
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border hover:bg-muted text-foreground'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-card border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {transferModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Transfer Call
            </h3>
            
            <div className="mb-4">
              <p className="text-muted-foreground text-sm mb-2">Session:</p>
              <p className="text-foreground font-mono text-sm">
                {transferModal.session?.id.slice(0, 8)}... ({transferModal.session?.agent_name})
              </p>
            </div>

            {message && (
              <div className={`p-3 rounded mb-4 ${
                message.includes('successfully') 
                  ? 'bg-success/10 border border-success/30 text-success'
                  : 'bg-destructive/10 border border-destructive/30 text-destructive'
              }`}>
                {message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Target Phone Number
                </label>
                <input
                  type="tel"
                  value={transferData.target_number}
                  onChange={(e) => setTransferData({ ...transferData, target_number: e.target.value })}
                  placeholder="+15551234567"
                  className="w-full px-3 py-2 bg-background border border-border rounded text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must be in E.164 format (e.g., +15551234567)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Transfer Type
                </label>
                <AnimatedDropdown
                  value={transferData.transfer_type}
                  onChange={(value) => setTransferData({ ...transferData, transfer_type: value })}
                  options={[
                    { value: 'warm', label: 'Warm Transfer - Consult first' },
                    { value: 'cold', label: 'Cold Transfer - Direct transfer' }
                  ]}
                  placeholder="Select transfer type"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeTransferModal}
                disabled={transferring}
                className="px-4 py-2 bg-muted text-foreground rounded hover:bg-muted/80 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {transferring ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Transferring...
                  </div>
                ) : (
                  <>
                    <ArrowRightLeft className="w-4 h-4" />
                    Transfer Call
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Confirmation Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md mx-4 shadow-elegant">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Clean up abandoned sessions?
              </h3>
            </div>
            
            <p className="text-muted-foreground text-sm mb-6">
              This will mark sessions without recent heartbeat as abandoned. This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCleanupModal(false)}
                className="px-4 py-2 bg-muted/50 hover:bg-muted border border-border/60 text-foreground rounded-lg transition-all duration-300 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCleanup}
                className="px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 text-sm font-medium"
              >
                Confirm Cleanup
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SessionsList;
